import {
  Avatar,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Link,
  Stack,
  Text,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import React, { ReactNode, useContext, useEffect } from 'react';

import { NavLink, useNavigate } from 'react-router-dom';
import { ContextAuth } from '../../contexts/Authentication';
import { FiFileText, FiHome, FiList, FiLock, FiLogIn, FiLogOut, FiUser } from 'react-icons/fi';
import { lighten } from '../../utils/functions';

interface IOwnProps {
  onClose(): void;
  isOpen: boolean;
}

interface IListItem {
  icon: ReactNode;
  label: string;
  to: string;
  color?: string;
  onClick?(): void;
}

const MenuMobile: React.FC<IOwnProps> = ({ onClose, isOpen }) => {
  const navigate = useNavigate();
  const { signOut, isAuth, isMobile, candidate } = useContext(ContextAuth);

  const theme = useTheme();
  const primary = theme.semanticTokens.colors.primaryColor;

  useEffect(() => {
    if (isMobile === false) {
      onClose();
    }
  }, [isMobile]);

  const MenuItem = ({ icon, label, to, onClick, color }: IListItem) => (
    <NavLink to={to} onClick={onClick}>
      {({ isActive }) => {
        return (
          <HStack
            borderRadius={3}
            p={2}
            boxSizing="border-box"
            bg={isActive && !onClick ? 'secondaryColor' : undefined}
            color={isActive && !onClick ? 'white' : undefined}
            cursor="pointer"
            spacing={3}
            _hover={{ color: 'blue.500' }}
            transition="color 0.2s"
          >
            {icon}
            <Text fontWeight="bold" fontSize="sm" color={(isActive && !onClick && 'white') || color || 'gray.700'}>
              {label}
            </Text>
          </HStack>
        );
      }}
    </NavLink>
  );

  return (
    <>
      <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent display="flex" flexDirection="column">
          <DrawerCloseButton color="white" />
          <DrawerHeader
            borderBottomWidth="1px"
            bgGradient={`linear(to-t, primaryColor, ${lighten(primary, 10)}, ${lighten(primary, 30)})`}
            animation="gradientShift 6s ease-in-out infinite alternate"
            color="white"
          >
            <Box mt={2} textAlign="center">
              {isAuth ? (
                <VStack spacing={3}>
                  <Avatar
                    width="80px"
                    height="80px"
                    borderWidth="2px"
                    borderColor="secondaryColor"
                    name={candidate?.user?.full_name}
                    src={candidate?.user?.avatar}
                  />
                  <Box>
                    <Text fontSize="md" fontWeight="bold">
                      {candidate?.user?.full_name}
                    </Text>
                    <Text fontSize="sm" color="gray.300">
                      {candidate?.user?.email}
                    </Text>
                  </Box>
                </VStack>
              ) : (
                <VStack spacing={2}>
                  <Avatar size="lg" icon={<FiUser />} />
                  <Button
                    leftIcon={<FiLogIn />}
                    colorScheme="blue"
                    size="sm"
                    onClick={() => {
                      navigate('/login');
                      onClose();
                    }}
                  >
                    Fazer Login
                  </Button>
                </VStack>
              )}
            </Box>
          </DrawerHeader>

          <DrawerBody flex="1" py={6} display="flex" flexDirection="column" gap={1}>
            {(isAuth && (
              <>
                <MenuItem icon={<FiHome />} label="Processos Seletivos" to="/" />
                <MenuItem icon={<FiList />} label="Minhas Inscrições" to="/painel/inscricoes" />
                <MenuItem icon={<FiFileText />} label="Isenções" to="/painel/isencoes" />
                <MenuItem icon={<FiFileText />} label="Recursos" to="/painel/recursos" />
                <MenuItem icon={<FiUser />} label="Meus Dados" to="/painel/meus-dados" />
                <MenuItem icon={<FiLock />} label="Alterar Senha" to="/painel/alterar-senha" />
                <MenuItem
                  icon={<FiLogOut />}
                  label="Sair"
                  to="/"
                  onClick={() => {
                    signOut();
                    onClose();
                  }}
                  color="red.500"
                />
              </>
            )) || (
              <Stack flex={1} justify="center" mt={-5}>
                <Text textAlign="center" color="gray.400" fontWeight="bold" textTransform="uppercase">
                  Você não está autenticado
                </Text>
              </Stack>
            )}
          </DrawerBody>

          <DrawerFooter borderTopWidth="1px" justifyContent="center" textAlign="center">
            <Text fontSize="xs" color="gray.500">
              Sistema desenvolvido por{' '}
              <Link href="https://www.adsr.com.br/" target="_blank" rel="noopener noreferrer" color="blue.500">
                ADSR Tecnologia
              </Link>
            </Text>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MenuMobile;
